import { z } from "zod";
import {
    AccountingReportCycle,
    AccountingReportCycleTotal,
    AccountingReportComparison,
    AccountingReportComparisonDiff,
    AccountingReportRatioType,
} from "../constants";
import { AccountingLedgerBasis } from "../types/domains/accounting/ledger";

/** One selected cell with semantic row/column identity + value snapshot. */
export const SpreadsheetCellRefSchema = z.object({
    /** Stable semantic row id, e.g. "section:revenue", "row:revenue:0", "total:revenue" */
    rowKey: z.string().min(1).max(500),
    /** Human-readable row label captured at send time */
    rowLabel: z.string().max(500),
    /** Column field key, e.g. "period_0", "period_1", "label" */
    columnKey: z.string().min(1).max(500),
    /** Human-readable column label captured at send time */
    columnLabel: z.string().max(500),
    /**
     * Raw numeric value snapshot (null for label/text cells).
     *
     * @security CLIENT-SUPPLIED AND UNVERIFIED — this value is provided by the
     * browser at send time and is NOT validated against server-side spreadsheet
     * data. It must never be used for business logic, calculations, or financial
     * computations. Treat as a display-only hint that may be stale or fabricated.
     */
    valueSnapshot: z.number().finite().nullable(),
});
export type SpreadsheetCellRef = z.infer<typeof SpreadsheetCellRefSchema>;

export const MAX_CELLS_PER_REFERENCE = 50;

/** All task types that support spreadsheet cell references in chat. */
export const SpreadsheetReferenceSourceSchema = z.enum([
    "profit_loss_activation",
    "balance_sheet",
]);
export type SpreadsheetReferenceSource = z.infer<
    typeof SpreadsheetReferenceSourceSchema
>;

/**
 * Snapshot of the report view configuration captured when a message is sent.
 * Used to determine whether a chip's referenced cells are still visible in
 * the currently mounted PL/BS task body, and to restore the view on demand.
 *
 * All fields are optional for backwards compatibility with messages persisted
 * before this schema was introduced.
 */
export const SpreadsheetViewConfigSchema = z.object({
    periodStart: z.string().date().optional(),
    periodEnd: z.string().date().optional(),
    cycle: z
        .union([
            z.nativeEnum(AccountingReportCycle),
            z.nativeEnum(AccountingReportCycleTotal),
        ])
        .optional(),
    comparisons: z.array(z.nativeEnum(AccountingReportComparison)).optional(),
    comparisonDiffs: z
        .array(z.nativeEnum(AccountingReportComparisonDiff))
        .optional(),
    ratioTypes: z.array(z.nativeEnum(AccountingReportRatioType)).optional(),
    /** PL only — BS omits this field. */
    ledgerBasis: z.nativeEnum(AccountingLedgerBasis).optional(),
});
export type SpreadsheetViewConfig = z.infer<typeof SpreadsheetViewConfigSchema>;

/**
 * Deep-equals two view configs for the purpose of determining whether a chip's
 * referenced cells are still in view. Treats missing arrays as empty.
 * `expandedIds` is intentionally excluded — auto-expansion on apply handles
 * cell visibility while keeping the comparison simple.
 */
export function isSameSpreadsheetViewConfig(
    a: SpreadsheetViewConfig | undefined,
    b: SpreadsheetViewConfig | undefined,
): boolean {
    if (a === b) {
        return true;
    }
    if (!a || !b) {
        return false;
    }
    if (a.periodStart !== b.periodStart) {
        return false;
    }
    if (a.periodEnd !== b.periodEnd) {
        return false;
    }
    if (a.cycle !== b.cycle) {
        return false;
    }
    if (a.ledgerBasis !== b.ledgerBasis) {
        return false;
    }
    const sortedEq = (
        xs: string[] | undefined,
        ys: string[] | undefined,
    ): boolean => {
        const x = [...(xs ?? [])].sort();
        const y = [...(ys ?? [])].sort();
        return x.length === y.length && x.every((v, i) => v === y[i]);
    };
    return (
        sortedEq(a.comparisons, b.comparisons) &&
        sortedEq(a.comparisonDiffs, b.comparisonDiffs) &&
        sortedEq(a.ratioTypes, b.ratioTypes)
    );
}

/** A group of selected cells from a specific spreadsheet source. */
export const SpreadsheetReferenceSchema = z.object({
    /** Source identifier — used to route click-to-focus back to the correct spreadsheet */
    source: SpreadsheetReferenceSourceSchema,
    /** Task the spreadsheet belongs to */
    taskId: z.string().uuid(),
    /** One or more selected cells */
    cells: z
        .array(SpreadsheetCellRefSchema)
        .min(1)
        .max(MAX_CELLS_PER_REFERENCE),
    /**
     * Optional human-readable range label, e.g. "Revenue · Jan–Mar 2025"
     * Generated client-side for display; not required for server persistence.
     */
    rangeLabel: z.string().max(500).optional(),
    /**
     * Snapshot of the report view when this reference was created.
     * Undefined for messages sent before this field was introduced.
     */
    viewConfig: SpreadsheetViewConfigSchema.optional(),
});
export type SpreadsheetReference = z.infer<typeof SpreadsheetReferenceSchema>;
